import React, { useCallback, useState } from 'react';

import { BodyText, FlexContainer, Icon, Link, Spinner } from '@placer-ui/components';
import type { Comment } from 'core/entities';
import { Attachment } from 'core/entities';
import { tableDateFormatter } from 'utils/table-formatters/table-formatters';
import { XTRA_CONTENT_VERSION_ENDPOINT } from 'API/offline-reports-api';
import { userCookieStorage } from 'core/authentification';
import { downloadFile } from 'API/common/download-file-service/download-file-service';

import styles from './support-request-comment.module.scss';

export const SupportRequestComment = ({ comment }: { comment: Comment }) => {
    const { id, html_body, created_at, author_meta, attachments } = comment;
    const [isDownloadingMap, setIsDownloadingMap] = useState<Record<string, boolean>>({});

    const downloadAttachment = useCallback(
        async ({ id: attachmentId, file_name }: Attachment) => {
            setIsDownloadingMap((prev) => ({ ...prev, [attachmentId]: true }));
            try {
                // We are downloading the file like this because we need to set the Authorization header
                const response = await fetch(`${XTRA_CONTENT_VERSION_ENDPOINT}/${attachmentId}`, {
                    headers: {
                        Authorization: `Token ${userCookieStorage.token}`,
                    },
                });
                await downloadFile(response, file_name);
            } finally {
                setIsDownloadingMap((prev) => ({ ...prev, [attachmentId]: false }));
            }
        },
        [setIsDownloadingMap],
    );

    const authorName = author_meta.fromName || author_meta.fromAddress || author_meta.name;

    return (
        <div key={id} data-testid="support-modal-comment">
            <div className={styles.commentHeader}>
                <BodyText variant="bold" testId="support-modal-author-name">
                    {authorName}
                </BodyText>
                <BodyText color="secondary" testId="support-modal-comment-creation-date">
                    {tableDateFormatter(created_at)}
                </BodyText>
            </div>
            <span
                dangerouslySetInnerHTML={{ __html: html_body }}
                className={styles.commentBody}
                data-testid="support-modal-comment-body"
            />
            {!!attachments.length && (
                <FlexContainer
                    gap={16}
                    flexWrap="wrap"
                    className={styles.attachments}
                    data-testid="support-request-comment-attachments"
                >
                    {attachments.map((attachment) => (
                        //TODO replace with FileCard from ui-components
                        <Link
                            onClick={() => downloadAttachment(attachment)}
                            download
                            color="gray"
                            data-testid={`support-request-comment-attachment-${attachment.id}`}
                        >
                            <div className={styles.attachment}>
                                <Icon type="Paperclip" allowHoverState={false} size={18} />
                                <BodyText size="small" variant="semi-bold">
                                    {attachment.file_name}
                                </BodyText>
                                {isDownloadingMap[attachment.id] ? (
                                    <Spinner variant="brand" />
                                ) : (
                                    <Icon type="DownloadSimple" size={18} />
                                )}
                            </div>
                        </Link>
                    ))}
                </FlexContainer>
            )}
        </div>
    );
};
